import {Button, ButtonBase, ButtonProps, CircularProgress, SvgIcon, Tooltip} from "@mui/material";
import {ReactElement, useEffect, useState} from "react";
import {VantageSiteBuildStatus} from "@/libwyze-common/api/types";
import {
  BuildCircleOutlined,
  CheckCircleOutlineOutlined,
  CloudOffOutlined,
  ReportProblemOutlined
} from "@mui/icons-material";
import {VantageApi} from "@/vantage-shared/vantage_api";
import {$KTM, KeyMetadatas} from "@/shared/types";
import {BaseApp} from "@/shared/BaseApp";
import {StyledBoxColumn, StyledEmpty} from "@/shared/StyledComponents";
import {sleep} from "@/shared/sleep";

type BuildStatusMetadata = {
  title: string,
  text: string,
  iconType: typeof SvgIcon,
}

const BUILD_STATUS_KTM = new KeyMetadatas<BuildStatusMetadata>([
  $KTM(VantageSiteBuildStatus.NONE, {
    title: "Build status not available",
    text: "You may not have published the site or the site may have been deleted.",
    iconType: CloudOffOutlined
  }),
  $KTM(VantageSiteBuildStatus.ERROR, {
    title: "Site build failed",
    text: "The last build attempt failed. You may try again if it is due to a transient failure.",
    iconType: ReportProblemOutlined
  }),
  $KTM(VantageSiteBuildStatus.QUEUED, {
    title: "Build in progress",
    text: "Build is queued, please wait...",
    iconType: BuildCircleOutlined
  }),
  $KTM(VantageSiteBuildStatus.BUILDING, {
    title: "Build in progress",
    text: "Build is running, please wait...",
    iconType: BuildCircleOutlined
  }),
  $KTM(VantageSiteBuildStatus.SUCCESS, {
    title: "Site built successfully",
    text: "The last build attempt succeeded and your site is now published.",
    iconType: CheckCircleOutlineOutlined
  }),
]);

async function fetchBuildStatus(practiceId: string, siteId: string): Promise<VantageSiteBuildStatus | null> {
  let status: VantageSiteBuildStatus;
  try {
    status = await VantageApi.getInstance().fetchVantageSiteBuildStatus(practiceId, siteId);
  } catch (e) {
    console.error(e);
    // Continue
  }
  return status || null;
}

function BuildStatusButton(props: {
  practiceId: string,
  siteId: string,
  buildStatus: VantageSiteBuildStatus,
  setBuildStatus: (status: VantageSiteBuildStatus) => void
}): ReactElement {
  const meta = BUILD_STATUS_KTM.findMetadata(props.buildStatus || VantageSiteBuildStatus.NONE);
  useEffect(() => {
    fetchBuildStatus(props.practiceId, props.siteId)
      .then(status => props.setBuildStatus(status));
  }, []);
  return <Tooltip title={meta.title}>
    <ButtonBase onClick={() => {
      BaseApp.CONTEXT.showCustomDialog("Build status", <StyledBoxColumn
        style={{alignItems: "center"}}>
        <StyledEmpty emptyConfig={{
          title: meta.title,
          text: meta.text,
          iconType: meta.iconType,
        }}/>
      </StyledBoxColumn>);
    }}>
      <meta.iconType style={{width: 16, height: 16, opacity: 0.5}}/>
    </ButtonBase>
  </Tooltip>;
}

export function PublishButton(props: ButtonProps & {
  practiceId: string,
  siteId: string,
  publishAttemptAt: number
}): ReactElement {
  const [buildStatus, setBuildStatus] = useState<VantageSiteBuildStatus>(props.publishAttemptAt > 0 ? VantageSiteBuildStatus.QUEUED : undefined);
  useEffect(() => {
    (async function () {
      while (true) {
        const status = await fetchBuildStatus(props.practiceId, props.siteId);
        console.log("Status: " + status);
        setBuildStatus(status || null);
        if (status !== VantageSiteBuildStatus.QUEUED && status !== VantageSiteBuildStatus.BUILDING) {
          break;
        }
        await sleep(5 * 60 * 1000); // Every 5 minutes.
      }
    })();
  }, [props.publishAttemptAt]);
  return <div style={{display: "flex", gap: 8, alignItems: "center"}}>
    <Button
      {...props}
      disabled={props.disabled
        || (buildStatus === VantageSiteBuildStatus.QUEUED || buildStatus === VantageSiteBuildStatus.BUILDING)}
    />
    {buildStatus !== undefined
      ? <BuildStatusButton practiceId={props.practiceId} siteId={props.siteId} buildStatus={buildStatus}
                           setBuildStatus={setBuildStatus}/>
      : <CircularProgress style={{width: 16, height: 16}}/>}
  </div>;
}